import React, { useState, useEffect } from 'react';
import { getCurrentUser } from '../util/amplifyAuth';
import { retrieveUserFiles, getRecommendations } from '../components/API';
import { useCachedTestNameMap, useCachedFxRange } from '../hooks/useCachedData';
import { isBloodTest, formatDate } from '../util/GenericUtil';
import DiagnosticResults from '../components/DiagnosticResults/DiagnosticResults';
import Spinner from '../components/Spinner/Spinner';
import '../styles/TestAnalysis.css';

function TestAnalysis() {
    const [user, setUser] = useState(null);
    const [userFiles, setUserFiles] = useState(null);
    const [selectedFile, setSelectedFile] = useState("all");
    const [loadingFiles, setLoadingFiles] = useState(false);
    const [diagnosticResults, setDiagnosticResults] = useState(null);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [error, setError] = useState('');

    const { normalizeTestName } = useCachedTestNameMap();
    const { getFxRange } = useCachedFxRange();

    useEffect(() => {
        getCurrentUser()
            .then(userData => setUser(userData))
            .catch(() => setUser(null));
    }, []);

    useEffect(() => {
        if (user && !userFiles) {
            retrieveUserFiles(setLoadingFiles, normalizeTestName).then(res => {
                setUserFiles(res);
            });
        }
    }, [user, normalizeTestName]);

    // Get filtered tests based on selected file (similar to Home.jsx)
    const getSelectedLabTests = () => {
        if (!userFiles) return [];

        if (selectedFile === "all") {
            let allTests = [];
            userFiles.forEach(file => {
                if (isBloodTest(file)) {
                    allTests.push(...file.data.body);
                }
            });
            return allTests;
        } else if (typeof selectedFile === 'number' && userFiles[selectedFile]) {
            return userFiles[selectedFile]?.data?.body || [];
        }

        return [];
    };

    const selectedLabTests = getSelectedLabTests();

    const handleAnalyze = async () => {
        if (!selectedLabTests || selectedLabTests.length === 0) {
            setError('No lab tests available for analysis. Please select a lab test file first.');
            return;
        }

        setIsAnalyzing(true);
        setError('');

        try {
            // Format data exactly like in Home.jsx
            const lab_data_list = [{
                header: {
                    testdate: new Date().toISOString().split('T')[0],
                    patient_id: 'test-analysis-user'
                },
                body: selectedLabTests.map(test => ({
                    test_name: test.test_name || test.alias,
                    result: parseFloat(test.result),
                    lab_low: parseFloat(test.lab_low),
                    lab_high: parseFloat(test.lab_high),
                    units: test.units,
                    alias: test.alias || normalizeTestName(test.test_name)
                }))
            }];

            // Create fx_data object using getFxRange method
            const fx_data = {
                body: {}
            };

            selectedLabTests.forEach(test => {
                const testName = test.alias || test.test_name;
                const fxRange = getFxRange(testName, 'male'); // Default to male for analysis
                if (fxRange) {
                    fx_data.body[testName] = {
                        fx_low: parseFloat(fxRange.fx_low),
                        fx_high: parseFloat(fxRange.fx_high),
                        units: fxRange.units
                    };
                }
            });

            console.log('=== TestAnalysis API Call ===');
            console.log('lab_data_list:', lab_data_list);
            console.log('fx_data:', fx_data);

            const response = await getRecommendations(lab_data_list, fx_data);

            // Parse the response body if it's a string
            const parsedResponse = typeof response.body === 'string'
                ? JSON.parse(response.body)
                : response.body || response;

            setDiagnosticResults({
                pathologies: parsedResponse.pathologies || [],
                metadata: parsedResponse.metadata || {},
                labTests: selectedLabTests,
                selectedFile: selectedFile
            });

        } catch (error) {
            console.error('Analysis error:', error);
            setError(`Analysis failed: ${error.message}`);
        } finally {
            setIsAnalyzing(false);
        }
    };

    return (
        <div className="test-analysis">
            <div className="test-analysis-header">
                <h1>Test Analysis - Diagnostic Weight Calibration</h1>
                <p>Select lab tests and analyze diagnostic scoring for marker weight calibration</p>
            </div>

            <div className="test-analysis-controls">
                <button
                    onClick={handleAnalyze}
                    disabled={isAnalyzing || !selectedLabTests.length}
                    className="analyze-btn"
                >
                    {isAnalyzing ? 'Analyzing...' : 'Analyze Diagnostics'}
                </button>
            </div>

            {error && (
                <div className="error-message">
                    {error}
                </div>
            )}

            {/* Lab Test Selection Tabs */}
            <div className="lab-file-selection">
                <h3>Select Lab Test File:</h3>
                <div className="tabs">
                    <div
                        className={`tabEntry ${selectedFile === "all" ? "selected" : ""}`}
                        onClick={() => setSelectedFile("all")}
                    >
                        All Tests
                    </div>
                    {loadingFiles && <Spinner size={24} color="#666" />}
                    {userFiles?.filter(isBloodTest)?.map((file, i) => (
                        <div
                            key={i}
                            onClick={() => setSelectedFile(i)}
                            className={`tabEntry ${selectedFile === i ? "selected" : ""}`}
                        >
                            {file.data.header.testdate ? formatDate(file.data.header.testdate.toString()) : "No Date"}
                        </div>
                    ))}
                </div>
                {selectedLabTests.length > 0 && (
                    <div className="selection-status">
                        <p>Selected: <strong>{selectedLabTests.length} lab tests</strong></p>
                        {selectedFile !== "all" && (
                            <p>From file: {selectedFile >= 0 && userFiles[selectedFile]?.data?.header?.testdate
                                ? formatDate(userFiles[selectedFile].data.header.testdate.toString())
                                : "Unknown date"}</p>
                        )}
                    </div>
                )}
            </div>

            {diagnosticResults && (
                <DiagnosticResults results={diagnosticResults} />
            )}
        </div>
    );
}

export default TestAnalysis;